import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Heading,
  useToast,
  Button,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  IconButton,
  Card,
  CardBody,
  FormErrorMessage,
  Text,
  Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsPlus, BsTrash } from "react-icons/bs";
import ReactSelect from "react-select";
import procurementApi from "../services/procurementApi";
import organizationalStructureApi from "../services/organizationalStructureApi";
import { employeeService } from "../../employee/employeeService";

const WorkflowCreate = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    unit: "",
    department: "",
    branch: "",
  });
  const [stages, setStages] = useState([
    {
      id: Date.now(),
      stageName: "",
      approverType: "ROLE",
      approverRole: "",
      approverUsers: [],
      order: 1,
      isRequired: true,
    },
  ]);
  const [errors, setErrors] = useState({});

  // Business unit options from backend
  const [businessUnits, setBusinessUnits] = useState({
    units: [],
    departments: [],
    branches: [],
  });

  useEffect(() => {
    fetchEmployees();
    fetchBusinessUnits();
  }, []);

  const fetchBusinessUnits = async () => {
    try {
      const [units, departments, branches] = await Promise.all([
        organizationalStructureApi.getUnits({ isActive: true }),
        organizationalStructureApi.getDepartments({ isActive: true }),
        organizationalStructureApi.getBranches({ isActive: true }),
      ]);

      setBusinessUnits({
        units: (units || []).map((item) => item.name).filter(Boolean),
        departments: (departments || [])
          .map((item) => item.name)
          .filter(Boolean),
        branches: (branches || []).map((item) => item.name).filter(Boolean),
      });
    } catch (error) {
      console.error("Failed to load business units:", error);
      // Keep empty lists if master fetch fails
      setBusinessUnits({
        units: [],
        departments: [],
        branches: [],
      });
    }
  };

  const getEmployeeName = (emp) => {
    if (!emp) return "Unknown Employee";

    const firstName =
      emp.firstName || emp.first_name || emp.name?.split(" ")[0] || "";
    const lastName =
      emp.lastName || emp.last_name || emp.name?.split(" ")[1] || "";
    const fullName = emp.fullName || emp.full_name || emp.name || "";

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    if (fullName) {
      return fullName;
    }

    return emp.email || emp.employeeId || "Unknown";
  };

  const employeeOptions = employees.map((emp) => ({
    value: emp._id,
    label: `${getEmployeeName(emp)}${emp.role || emp.ROLE ? ` (${emp.role || emp.ROLE})` : ""}`,
  }));

  const fetchEmployees = async () => {
    try {
      const data = await employeeService.getAllEmployees();
      setEmployees(data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const addStage = () => {
    setStages([
      ...stages,
      {
        id: Date.now(),
        stageName: "",
        approverType: "ROLE",
        approverRole: "",
        approverUsers: [],
        order: stages.length + 1,
        isRequired: true,
      },
    ]);
  };

  const updateStage = (id, field, value) => {
    setStages(
      stages.map((stage) =>
        stage.id === id ? { ...stage, [field]: value } : stage,
      ),
    );
  };

  const removeStage = (id) => {
    setStages(stages.filter((stage) => stage.id !== id));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = "Workflow name must be at least 3 characters";
    }

    if (!formData.unit || formData.unit.trim().length === 0) {
      newErrors.unit = "Unit is required";
    }

    if (!formData.department || formData.department.trim().length === 0) {
      newErrors.department = "Department is required";
    }

    if (!formData.branch || formData.branch.trim().length === 0) {
      newErrors.branch = "Branch is required";
    }

    if (stages.length === 0) {
      newErrors.stages = "At least one stage is required";
      toast({
        title: "Validation Error",
        description: "Please add at least one approval stage",
        status: "error",
        duration: 3000,
      });
    }

    const invalidStages = stages.filter(
      (stage) =>
        !stage.stageName ||
        (stage.approverType === "ROLE" && !stage.approverRole) ||
        (stage.approverType === "USERS" &&
          (!stage.approverUsers || stage.approverUsers.length === 0)),
    );
    if (invalidStages.length > 0) {
      newErrors.stages = "All stages must have name and approver";
      toast({
        title: "Validation Error",
        description: "Please complete all stage fields (name and approver)",
        status: "error",
        duration: 3000,
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const workflowData = {
        ...formData,
        stages: stages.map(({ id, ...stage }) => ({
          ...stage,
          approverType: stage.approverType === "USERS" ? "USER" : "ROLE",
          approverUsers:
            stage.approverType === "USERS"
              ? (stage.approverUsers || []).slice(0, 1)
              : [],
        })),
      };
      await procurementApi.createWorkflow(workflowData);
      toast({
        title: "Workflow created successfully",
        status: "success",
        duration: 3000,
      });
      navigate("/procurement/workflows");
    } catch (error) {
      toast({
        title: "Error creating workflow",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/procurement/workflows");
  };

  return (
    <Box p={6}>
      <HStack mb={6}>
        <Button
          leftIcon={<BsArrowLeft />}
          variant="ghost"
          onClick={handleCancel}
        >
          Back
        </Button>
        <Heading size="lg">Create Workflow</Heading>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" borderWidth="1px">
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Workflow Details */}
            <FormControl isInvalid={errors.name} isRequired>
              <FormLabel>Workflow Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Standard Procurement Approval"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Describe this workflow"
                rows={3}
              />
            </FormControl>

            {/* Workflow Routing Fields */}
            <Box
              bg="blue.50"
              p={4}
              borderRadius="md"
              borderWidth="1px"
              borderColor="blue.200"
            >
              <Text fontSize="sm" fontWeight="semibold" color="blue.700" mb={3}>
                Workflow Auto-Selection Criteria
              </Text>
              <Text fontSize="xs" color="blue.600" mb={4}>
                This workflow will be automatically assigned to procurement
                requests matching the unit, department, and branch combination
                below.
              </Text>
              <VStack spacing={4} align="stretch">
                <FormControl isInvalid={errors.unit} isRequired>
                  <FormLabel fontSize="sm">Unit</FormLabel>
                  <Select
                    value={formData.unit}
                    onChange={(e) => handleInputChange("unit", e.target.value)}
                    placeholder="Select unit"
                    bg="white"
                  >
                    {businessUnits.units.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.unit}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.department} isRequired>
                  <FormLabel fontSize="sm">Department</FormLabel>
                  <Select
                    value={formData.department}
                    onChange={(e) =>
                      handleInputChange("department", e.target.value)
                    }
                    placeholder="Select department"
                    bg="white"
                  >
                    {businessUnits.departments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.department}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.branch} isRequired>
                  <FormLabel fontSize="sm">Branch</FormLabel>
                  <Select
                    value={formData.branch}
                    onChange={(e) =>
                      handleInputChange("branch", e.target.value)
                    }
                    placeholder="Select branch"
                    bg="white"
                  >
                    {businessUnits.branches.map((branch) => (
                      <option key={branch} value={branch}>
                        {branch}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.branch}</FormErrorMessage>
                </FormControl>
              </VStack>
            </Box>

            {/* Approval Stages */}
            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="sm">Approval Stages</Heading>
                <Button
                  leftIcon={<BsPlus />}
                  size="sm"
                  onClick={addStage}
                  variant="outline"
                  colorScheme="blue"
                >
                  Add Stage
                </Button>
              </HStack>

              <VStack spacing={4} align="stretch">
                {stages.map((stage, index) => (
                  <Card key={stage.id}>
                    <CardBody>
                      <VStack align="stretch" spacing={4}>
                        <HStack align="start" spacing={4}>
                          <FormControl flex={1} isRequired>
                            <FormLabel fontSize="sm">Stage Name</FormLabel>
                            <Input
                              size="sm"
                              value={stage.stageName}
                              onChange={(e) =>
                                updateStage(
                                  stage.id,
                                  "stageName",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g., Manager Approval"
                            />
                          </FormControl>

                          <FormControl maxW="150px">
                            <FormLabel fontSize="sm">Approver Type</FormLabel>
                            <Select
                              size="sm"
                              value={stage.approverType || "ROLE"}
                              onChange={(e) =>
                                updateStage(
                                  stage.id,
                                  "approverType",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="ROLE">By ROLE</option>
                              <option value="USERS">Specific USERS</option>
                            </Select>
                          </FormControl>

                          <FormControl maxW="100px">
                            <FormLabel fontSize="sm">Order</FormLabel>
                            <Input
                              size="sm"
                              type="number"
                              value={stage.order}
                              onChange={(e) =>
                                updateStage(
                                  stage.id,
                                  "order",
                                  parseInt(e.target.value) || 1,
                                )
                              }
                            />
                          </FormControl>

                          <IconButton
                            mt={7}
                            size="sm"
                            icon={<BsTrash />}
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeStage(stage.id)}
                            aria-label="Remove stage"
                            isDisabled={stages.length === 1}
                          />
                        </HStack>

                        {/* Approver ROLE Selection */}
                        {(stage.approverType === "ROLE" ||
                          !stage.approverType) && (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Approver ROLE</FormLabel>
                            <Select
                              size="sm"
                              value={stage.approverRole}
                              onChange={(e) =>
                                updateStage(
                                  stage.id,
                                  "approverRole",
                                  e.target.value,
                                )
                              }
                              placeholder="Select ROLE"
                            >
                              <option value="SuperAdmin">Super Admin</option>
                              <option value="Admin">Admin</option>
                              <option value="HR">HR</option>
                              <option value="Manager">Manager</option>
                              <option value="Employee">Employee</option>
                            </Select>
                          </FormControl>
                        )}

                        {/* Approver USERS Selection */}
                        {stage.approverType === "USERS" && (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">
                              Select Approver User
                              {stage.approverUsers?.length > 0 && (
                                <Badge ml={2} colorScheme="blue">
                                  1 selected
                                </Badge>
                              )}
                            </FormLabel>
                            {employees.length === 0 ? (
                              <Text fontSize="sm" color="gray.500">
                                No employees found. Please add employees first.
                              </Text>
                            ) : (
                              <VStack align="stretch" spacing={2}>
                                <Box>
                                  <ReactSelect
                                    placeholder="Search and select an approver"
                                    isClearable
                                    options={employeeOptions}
                                    value={
                                      employeeOptions.find(
                                        (option) =>
                                          option.value ===
                                          stage.approverUsers?.[0],
                                      ) || null
                                    }
                                    onChange={(selectedOption) =>
                                      updateStage(
                                        stage.id,
                                        "approverUsers",
                                        selectedOption
                                          ? [selectedOption.value]
                                          : [],
                                      )
                                    }
                                  />
                                </Box>
                                <Text fontSize="xs" color="gray.500">
                                  One stage can have only one specific user
                                  approver.
                                </Text>
                              </VStack>
                            )}
                          </FormControl>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            </Box>

            {/* Master data empty guard */}
            {!loading &&
              (businessUnits.units.length === 0 ||
                businessUnits.departments.length === 0 ||
                businessUnits.branches.length === 0) && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Some master data is missing. Please add{" "}
                    {businessUnits.units.length === 0 ? "Units" : ""}
                    {businessUnits.units.length === 0 &&
                    businessUnits.departments.length === 0
                      ? ", "
                      : ""}
                    {businessUnits.departments.length === 0
                      ? "Departments"
                      : ""}
                    {(businessUnits.units.length === 0 ||
                      businessUnits.departments.length === 0) &&
                    businessUnits.branches.length === 0
                      ? ", "
                      : ""}
                    {businessUnits.branches.length === 0 ? "Branches" : ""} in{" "}
                    <strong>Organizational Structure Maintenance</strong> first.
                  </Text>
                </Alert>
              )}

            {/* Action Buttons */}
            <HStack spacing={4} justify="flex-end" pt={4}>
              <Button
                variant="outline"
                onClick={handleCancel}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                isDisabled={
                  businessUnits.units.length === 0 ||
                  businessUnits.departments.length === 0 ||
                  businessUnits.branches.length === 0
                }
                title={
                  businessUnits.units.length === 0 ||
                  businessUnits.departments.length === 0 ||
                  businessUnits.branches.length === 0
                    ? "Please populate master data before creating a workflow"
                    : undefined
                }
              >
                Create Workflow
              </Button>
            </HStack>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default WorkflowCreate;
